/**
 * feat-325 — every string the fill-blank Grading mode control and the
 * "Try an answer" box put on screen, in ONE place (on-screen copy renders
 * through constants so a literal-text grep cannot return a true zero).
 *
 * Copy authority: design/01-grading-mode.html and design/02-try-answer-states.html
 * (rendered by design/_panel.js).
 */

import type { BlankGradingMode, BlankTestLocale } from '../../../services/quizContentAdminService';

/** UI choice. `auto` is the UI name for the stored `null`. */
export type GradingModeChoice = 'auto' | BlankGradingMode;

export const toGradingModeChoice = (m: BlankGradingMode | null | undefined): GradingModeChoice =>
  m ?? 'auto';
export const fromGradingModeChoice = (c: GradingModeChoice): BlankGradingMode | null =>
  c === 'auto' ? null : c;

export const GRADING_MODE_CHOICES: GradingModeChoice[] = ['auto', 'strict', 'lenient'];

export const BLANK_GRADING_COPY = {
  label: 'Grading mode',
  modeName: {
    auto: 'Auto',
    strict: 'Strict',
    lenient: 'Lenient',
  } as Record<GradingModeChoice, string>,
  help: {
    auto: 'Auto is strict when the question contains quotes or the answer is the company name. Otherwise lenient.',
    strict: 'Same words in the same order. Typos and capitalization are forgiven.',
    lenient: 'Any reasonable, on-topic answer passes.',
  } as Record<GradingModeChoice, string>,
} as const;

export const TRY_ANSWER_COPY = {
  heading: 'Try an answer',
  sideNote: 'Nothing is saved · no XP',
  inputLabel: 'Answer to test',
  placeholder: 'Type what a crew member might say',
  gradeIn: 'Grade in',
  localeGroupLabel: 'Grade in language',
  localeName: { en: 'EN', es: 'ES' } as Record<BlankTestLocale, string>,
  test: 'Test',
  testing: 'Testing…',
  locked: 'Available once this question is saved. Fill in the question text above.',
  waiting: 'Checking with the AI — up to 3 seconds…',
  correct: '✓ Correct',
  incorrect: '✕ Incorrect',
  viaAuto: ' · via Auto',
  exactMatch: 'Exact match — AI not consulted',
  tested: (answer: string, locale: BlankTestLocale) =>
    `Tested “${answer}” · ${TRY_ANSWER_COPY.localeName[locale]}`,
  latency: (ms: number) => `${(ms / 1000).toFixed(1)} s`,
  fallbackTitle: 'The AI check didn’t answer',
  fallbackConsequence: 'A crew member giving this answer would get the multiple-choice fallback.',
  errorTitle: 'Couldn’t run the test.',
  errorBody: 'Check your connection and try again.',
  tryAgain: 'Try again',
};
